import { useState } from 'react';
import {
  cloneRig,
  point,
  matrix,
  transform,
  uniqueName,
  type ConstraintTarget,
  type ConstraintType,
  type RigConstraint,
  type RigDoc,
  type Vec3,
} from '../../lib/rig-doc.js';
import {
  buildAdjacency,
  clean,
  gradient,
  levels,
  limitTotal,
  mirrorWeights,
  nodeWeights,
  normalizeAll,
  smooth,
  transferWeights,
  applyToRig,
  type VertexWeights,
} from '../../lib/rig/weights.js';
import { fitHuman, fitQuadruped, symmetrize } from '../../lib/rig/autorig.js';
import { useRig } from '../../state/rig.js';
import { Card, IconButton, MenuButton, NumberField, PropRow, SelectField, Toggle, VecField } from '../ui/controls.js';

const TYPES: { type: ConstraintType; label: string; hint: string }[] = [
  { type: 'twoBoneIk', label: 'Two-bone IK', hint: 'Foot / hand reaches a target, knee / elbow bends toward a pole' },
  { type: 'chainIk', label: 'Chain IK', hint: 'Any number of joints reach a target (tails, tentacles, spines)' },
  { type: 'copyRotation', label: 'Copy rotation', hint: 'Match another joint’s rotation' },
  { type: 'copyLocation', label: 'Copy location', hint: 'Follow another joint or point' },
  { type: 'limitRotation', label: 'Limit rotation', hint: 'Clamp local rotation per axis (degrees)' },
  { type: 'dampedTrack', label: 'Damped track', hint: 'Point an axis at a target with the smallest turn' },
  { type: 'lookAt', label: 'Look at', hint: 'Aim an axis at a target, keep up toward +Y (eyes, heads)' },
  { type: 'childOf', label: 'Child of', hint: 'Follow another joint as if parented (props switching hands)' },
];
const AXES = ['x', 'y', 'z', '-x', '-y', '-z'].map((a) => ({ value: a, label: a.toUpperCase() }));

function commit(edit: (rig: RigDoc) => void) {
  const s = useRig.getState(),
    next = cloneRig(s.rig);
  edit(next);
  s.commit(next);
}

function defaults(type: ConstraintType, bone: string, target: string): RigConstraint {
  switch (type) {
    case 'twoBoneIk':
      return { type, bone, target };
    case 'chainIk':
      return { type, bone, target, chainLength: 3 };
    case 'copyRotation':
    case 'childOf':
      return { type, bone, target };
    case 'copyLocation':
      return { type, bone, target };
    case 'limitRotation':
      return { type, bone, min: [-90, null, null], max: [90, null, null] };
    case 'dampedTrack':
      return { type, bone, target, axis: 'y' };
    case 'lookAt':
      return { type, bone, target, aim: 'z', up: 'y' };
  }
}

/** Joint-or-point target editor. */
function TargetField({
  value,
  joints,
  optional,
  onChange,
}: {
  value: ConstraintTarget | undefined;
  joints: string[];
  optional?: boolean;
  onChange: (v: ConstraintTarget | undefined) => void;
}) {
  const kind = value === undefined ? 'none' : Array.isArray(value) ? 'point' : 'joint';
  return (
    <div className="rig-target">
      <SelectField
        value={kind}
        options={[...(optional ? [{ value: 'none', label: 'None' }] : []), { value: 'joint', label: 'Joint' }, { value: 'point', label: 'Point' }]}
        onChange={(k) => onChange(k === 'none' ? undefined : k === 'point' ? [0, 1, 0] : (joints[0] ?? ''))}
      />
      {kind === 'joint' && <SelectField value={value as string} options={joints} onChange={(v) => onChange(v)} />}
      {kind === 'point' && <VecField value={value as Vec3} onChange={(v) => onChange(v as Vec3)} />}
    </div>
  );
}

/**
 * One-click foot / hand IK (Blender-style): an unparented control joint at the
 * end joint (the IK target), a pole joint in front of the middle joint, both
 * non-deforming, and a two-bone IK constraint using them.
 */
export function addLimbIk(rig: RigDoc, bone: string): RigDoc | string {
  const end = rig.joints.find((j) => j.name === bone);
  const mid = rig.joints.find((j) => j.name === end?.parent);
  const root = rig.joints.find((j) => j.name === mid?.parent);
  if (!end || !mid || !root) return 'Select a foot or hand: the joint needs a parent and a grandparent.';
  const p = (name: string) => point(matrix(transform(rig.joints.find((j) => j.name === name)!.bind)), [0, 0, 0]);
  const a = p(root.name),
    b = p(mid.name),
    c = p(end.name);
  // Pole: in front of the knee / elbow along its bend, else +Z (glTF forward).
  const ac = c.map((v, i) => v - a[i]!) as Vec3;
  const ab = b.map((v, i) => v - a[i]!) as Vec3;
  const len2 = ac.reduce((s, v) => s + v * v, 0) || 1;
  const k = ab.reduce((s, v, i) => s + v * ac[i]!, 0) / len2;
  let bend = ab.map((v, i) => v - ac[i]! * k) as Vec3;
  const bl = Math.hypot(...bend);
  const limb = Math.hypot(...ab) + Math.hypot(...c.map((v, i) => v - b[i]!));
  bend = bl > limb * 0.01 ? (bend.map((v) => v / bl) as Vec3) : [0, 0, 1];
  const next = cloneRig(rig);
  const names = next.joints.map((j) => j.name);
  const target = uniqueName(`${bone}.ik`, names);
  const pole = uniqueName(`${mid.name}.pole`, [...names, target]);
  next.joints.push({ name: target, parent: null, bind: { ...transform(end.bind) }, deform: false });
  next.joints.push({
    name: pole,
    parent: null,
    bind: { position: b.map((v, i) => v + bend[i]! * limb * 0.75) as Vec3, rotation: [0, 0, 0], scale: [1, 1, 1] },
    deform: false,
  });
  next.constraints ??= [];
  next.constraints.push({ type: 'twoBoneIk', name: `${bone} IK`, bone, target, pole, targetRotation: true });
  return next;
}

export function ConstraintsPanel() {
  const rig = useRig((s) => s.rig),
    selected = useRig((s) => s.selected);
  const joints = rig.joints.map((j) => j.name);
  const list = rig.constraints ?? [];
  const edit = (i: number, fn: (c: RigConstraint) => void) =>
    commit((r) => {
      const c = r.constraints?.[i];
      if (c) fn(c);
    });
  const setField = (i: number, key: string, value: unknown) =>
    edit(i, (c) => {
      const rec = c as unknown as Record<string, unknown>;
      if (value === undefined) delete rec[key];
      else rec[key] = value;
    });
  const [error, setError] = useState('');
  return (
    <Card
      title="Constraints & IK"
      subtitle={list.length ? `${list.length}` : undefined}
      actions={
        <MenuButton
          className="ghost small"
          title="Add a constraint to the selected joint"
          items={TYPES.map((t) => ({
            id: t.type,
            label: t.label,
            disabled: !selected,
            run: () =>
              commit((r) => {
                r.constraints ??= [];
                r.constraints.push(defaults(t.type, selected!, joints.find((j) => j !== selected) ?? selected!));
              }),
          }))}
        >
          + Add
        </MenuButton>
      }
    >
      <div className="rig-button-row">
        <button
          type="button"
          className="ghost small"
          disabled={!selected}
          title="Adds an IK target and pole control joint for the selected foot / hand and a two-bone IK constraint"
          onClick={() => {
            if (!selected) return;
            const next = addLimbIk(useRig.getState().rig, selected);
            if (typeof next === 'string') setError(next);
            else {
              setError('');
              useRig.getState().commit(next);
            }
          }}
        >
          Add limb IK to {selected ?? 'joint'}
        </button>
      </div>
      {error && <p className="rig-help danger">{error}</p>}
      {!list.length && <p className="muted rig-help">Constraints run in order after animation, in the editor and in the game.</p>}
      {list.map((c, i) => {
        const missing = [c.bone, ...('target' in c && typeof c.target === 'string' ? [c.target] : [])].filter((n) => !joints.includes(n));
        return (
          <div key={i} className={`rig-constraint${c.enabled === false ? ' is-off' : ''}`}>
            <div className="rig-constraint-head">
              <Toggle checked={c.enabled !== false} onChange={(on) => setField(i, 'enabled', on ? undefined : false)} />
              <strong title={TYPES.find((t) => t.type === c.type)?.hint}>{c.name || TYPES.find((t) => t.type === c.type)?.label}</strong>
              <span className="muted">{c.bone}</span>
              <IconButton icon="arrowUp" size={12} title="Move up (evaluated earlier)" disabled={i === 0} onClick={() => commit((r) => r.constraints!.splice(i - 1, 0, r.constraints!.splice(i, 1)[0]!))} />
              <IconButton icon="arrowDown" size={12} title="Move down" disabled={i === list.length - 1} onClick={() => commit((r) => r.constraints!.splice(i + 1, 0, r.constraints!.splice(i, 1)[0]!))} />
              <IconButton icon="trash" size={12} title="Delete constraint" onClick={() => commit((r) => r.constraints!.splice(i, 1))} />
            </div>
            {missing.length > 0 && <p className="rig-help danger">Unknown joint: {missing.join(', ')}</p>}
            <PropRow label="Bone">
              <SelectField value={c.bone} options={joints} onChange={(v) => setField(i, 'bone', v)} />
            </PropRow>
            {'target' in c && (c.type === 'copyRotation' || c.type === 'childOf') && (
              <PropRow label="Target">
                <SelectField value={c.target} options={joints} onChange={(v) => setField(i, 'target', v)} />
              </PropRow>
            )}
            {'target' in c && c.type !== 'copyRotation' && c.type !== 'childOf' && (
              <PropRow label="Target">
                <TargetField value={c.target} joints={joints} onChange={(v) => setField(i, 'target', v ?? joints[0])} />
              </PropRow>
            )}
            {(c.type === 'twoBoneIk' || c.type === 'chainIk') && (
              <PropRow label="Pole" hint="The knee / elbow bends toward this">
                <TargetField value={c.pole} joints={joints} optional onChange={(v) => setField(i, 'pole', v)} />
              </PropRow>
            )}
            {c.type === 'twoBoneIk' && (
              <div className="rig-check">
                <Toggle checked={c.targetRotation === true} onChange={(on) => setField(i, 'targetRotation', on || undefined)} label="Copy target rotation" />
              </div>
            )}
            {c.type === 'chainIk' && (
              <>
                <PropRow label="Chain length">
                  <NumberField value={c.chainLength} integer min={1} max={64} onChange={(v) => setField(i, 'chainLength', Math.max(1, Math.round(v)))} />
                </PropRow>
                <PropRow label="Iterations">
                  <NumberField value={c.iterations ?? 16} integer min={1} max={64} onChange={(v) => setField(i, 'iterations', Math.round(v))} />
                </PropRow>
              </>
            )}
            {(c.type === 'copyRotation' || c.type === 'copyLocation') && (
              <>
                <PropRow label="Space">
                  <SelectField
                    value={c.space ?? 'world'}
                    options={[
                      { value: 'world', label: 'Model (world)' },
                      { value: 'local', label: 'Local' },
                    ]}
                    onChange={(v) => setField(i, 'space', v === 'world' ? undefined : v)}
                  />
                </PropRow>
                <PropRow label="Axes">
                  <div className="rig-axes">
                    {(['X', 'Y', 'Z'] as const).map((a, k) => (
                      <Toggle
                        key={a}
                        label={a}
                        checked={(c.axes ?? [true, true, true])[k]!}
                        onChange={(on) => {
                          const axes = [...(c.axes ?? [true, true, true])] as [boolean, boolean, boolean];
                          axes[k] = on;
                          setField(i, 'axes', axes.every(Boolean) ? undefined : axes);
                        }}
                      />
                    ))}
                  </div>
                </PropRow>
                {c.type === 'copyRotation' ? (
                  <div className="rig-check">
                    <Toggle checked={c.mixAdd === true} onChange={(on) => setField(i, 'mixAdd', on || undefined)} label="Add to own rotation" />
                  </div>
                ) : (
                  <div className="rig-check">
                    <Toggle checked={c.offset === true} onChange={(on) => setField(i, 'offset', on || undefined)} label="Keep offset" />
                  </div>
                )}
              </>
            )}
            {c.type === 'limitRotation' &&
              (['X', 'Y', 'Z'] as const).map((a, k) => {
                const min = c.min?.[k] ?? null,
                  max = c.max?.[k] ?? null;
                const set = (lo: number | null, hi: number | null) =>
                  edit(i, (x) => {
                    if (x.type !== 'limitRotation') return;
                    type Limits = [number | null, number | null, number | null];
                    const nextMin = [...(x.min ?? [null, null, null])] as Limits;
                    const nextMax = [...(x.max ?? [null, null, null])] as Limits;
                    nextMin[k] = lo;
                    nextMax[k] = hi;
                    x.min = nextMin;
                    x.max = nextMax;
                  });
                return (
                  <PropRow key={a} label={`Limit ${a}`}>
                    <div className="rig-limit">
                      <Toggle checked={min !== null || max !== null} onChange={(on) => set(on ? -45 : null, on ? 45 : null)} />
                      {(min !== null || max !== null) && (
                        <>
                          <NumberField value={min ?? -180} min={-180} max={180} step={1} onChange={(v) => set(v, max)} />
                          <NumberField value={max ?? 180} min={-180} max={180} step={1} onChange={(v) => set(min, v)} />
                        </>
                      )}
                    </div>
                  </PropRow>
                );
              })}
            {c.type === 'dampedTrack' && (
              <PropRow label="Axis">
                <SelectField value={c.axis ?? 'y'} options={AXES} onChange={(v) => setField(i, 'axis', v)} />
              </PropRow>
            )}
            {c.type === 'lookAt' && (
              <>
                <PropRow label="Aim axis">
                  <SelectField value={c.aim ?? 'z'} options={AXES} onChange={(v) => setField(i, 'aim', v)} />
                </PropRow>
                <PropRow label="Up axis">
                  <SelectField value={c.up ?? 'y'} options={AXES} onChange={(v) => setField(i, 'up', v)} />
                </PropRow>
              </>
            )}
            <PropRow label="Influence">
              <NumberField value={c.influence ?? 1} min={0} max={1} step={0.05} onChange={(v) => setField(i, 'influence', v >= 1 ? undefined : Math.max(0, v))} />
            </PropRow>
          </div>
        );
      })}
      {selected && (
        <div className="rig-check">
          <Toggle
            checked={rig.joints.find((j) => j.name === selected)?.deform !== false}
            onChange={(on) =>
              commit((r) => {
                const j = r.joints.find((x) => x.name === selected);
                if (!j) return;
                if (on) delete j.deform;
                else j.deform = false;
              })
            }
            label={`${selected} deforms the mesh`}
          />
        </div>
      )}
    </Card>
  );
}

/** Blender's weight tools on the current mesh node (all its primitives). */
export function WeightToolsCard() {
  const rig = useRig((s) => s.rig),
    geometry = useRig((s) => s.geometry),
    frame = useRig((s) => s.frame),
    node = useRig((s) => s.node),
    selected = useRig((s) => s.selected),
    locks = useRig((s) => s.weightLocks);
  const [smoothFactor, setSmoothFactor] = useState(0.5);
  const [iterations, setIterations] = useState(2);
  const [limit, setLimit] = useState(4);
  const [threshold, setThreshold] = useState(0.02);
  const [gain, setGain] = useState(1);
  const [offset, setOffset] = useState(0);
  const [direction, setDirection] = useState<'+to-' | '-to+' | 'both'>('+to-');
  const [source, setSource] = useState('');
  const [gradFrom, setGradFrom] = useState('');
  const [gradTo, setGradTo] = useState('');
  const [gradMode, setGradMode] = useState<'linear' | 'radial'>('linear');
  const [note, setNote] = useState('');
  if (!geometry || !frame) return null;
  const locked = new Set(locks);
  const meshes = geometry.meshes.map((m, i) => ({ m, i })).filter((x) => x.m.node === node);
  const nodes = [...new Set(geometry.meshes.map((m) => m.node))];
  const run = (label: string, fn: (w: VertexWeights, ctx: { positions: Vec3[]; indices: number[] }) => VertexWeights) => {
    let next = useRig.getState().rig;
    for (const { i } of meshes) next = applyToRig(next, geometry, frame, i, fn);
    useRig.getState().commit(next);
    setNote(`${label} applied to ${node}.`);
  };
  const jointPos = (name: string) => frame.joints.find((j) => j.name === name)?.position;
  return (
    <Card title="Weight tools" subtitle={node}>
      <div className="rig-button-row">
        <button type="button" className="ghost small" onClick={() => run('Normalize all', (w) => normalizeAll(w, { locked }))}>
          Normalize all
        </button>
        <button
          type="button"
          className="ghost small"
          onClick={() => run('Smooth', (w, m) => smooth(w, buildAdjacency(m.indices, w.length, m.positions), { factor: smoothFactor, iterations, locked }))}
        >
          Smooth
        </button>
        <button type="button" className="ghost small" onClick={() => run(`Limit total ${limit}`, (w) => limitTotal(w, limit, { locked }))}>
          Limit total
        </button>
        <button type="button" className="ghost small" onClick={() => run('Clean', (w) => clean(w, { threshold, locked }))}>
          Clean
        </button>
      </div>
      <PropRow label="Smooth">
        <div className="rig-limit">
          <NumberField value={smoothFactor} min={0} max={1} step={0.05} onChange={setSmoothFactor} />
          <NumberField value={iterations} integer min={1} max={50} onChange={(v) => setIterations(Math.max(1, Math.round(v)))} />
        </div>
      </PropRow>
      <PropRow label="Limit / clean">
        <div className="rig-limit">
          <NumberField value={limit} integer min={1} max={4} onChange={(v) => setLimit(Math.max(1, Math.min(4, Math.round(v))))} />
          <NumberField value={threshold} min={0} max={0.5} step={0.005} onChange={setThreshold} />
        </div>
      </PropRow>
      <PropRow label="Levels" hint="weight × gain + offset for the selected joint">
        <div className="rig-limit">
          <NumberField value={gain} min={0} max={4} step={0.05} onChange={setGain} />
          <NumberField value={offset} min={-1} max={1} step={0.05} onChange={setOffset} />
          <button
            type="button"
            className="ghost small"
            disabled={!selected}
            onClick={() => selected && run('Levels', (w) => levels(w, { gain, offset, joints: new Set([selected]), locked }))}
          >
            Apply
          </button>
        </div>
      </PropRow>
      <PropRow label="Mirror" hint="Copies weights across X, swapping .L / .R joint names">
        <div className="rig-limit">
          <SelectField
            value={direction}
            options={[
              { value: '+to-', label: '+X → −X' },
              { value: '-to+', label: '−X → +X' },
              { value: 'both', label: 'Swap sides' },
            ]}
            onChange={(v) => setDirection(v as typeof direction)}
          />
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              let unmatched = 0;
              run('Mirror', (w, m) => {
                const r = mirrorWeights(w, m.positions, { direction, tolerance: 1e-3, locked });
                unmatched += r.unmatched.length;
                return r.weights;
              });
              if (unmatched) setNote(`Mirrored; ${unmatched} vertices had no mirror partner.`);
            }}
          >
            Mirror
          </button>
        </div>
      </PropRow>
      <PropRow label="Transfer from" hint="Copies weights from the nearest faces of another mesh of this model">
        <div className="rig-limit">
          <SelectField value={source} placeholder="Source mesh" options={nodes.filter((n) => n !== node)} onChange={setSource} />
          <button
            type="button"
            className="ghost small"
            disabled={!source}
            onClick={() => {
              const src = geometry.meshes.map((m, i) => ({ m, i })).filter((x) => x.m.node === source);
              const srcWeights = nodeWeights(rig, geometry, frame, source);
              const positions: Vec3[] = [],
                indices: number[] = [],
                weights: VertexWeights = [];
              for (const { m, i } of src) {
                const base = positions.length;
                positions.push(...(frame.positions[i] ?? []));
                weights.push(...(srcWeights[m.primitive] ?? []));
                indices.push(...m.indices.map((x) => x + base));
              }
              run(`Transfer from ${source}`, (_w, m) => transferWeights(m.positions, { positions, indices, weights }, { mode: 'nearestFace' }).weights);
            }}
          >
            Transfer
          </button>
        </div>
      </PropRow>
      <PropRow label="Gradient" hint="Selected joint: weight 1 at the first joint fading to 0 at the second">
        <div className="rig-limit">
          <SelectField value={gradFrom} placeholder="From" options={rig.joints.map((j) => j.name)} onChange={setGradFrom} />
          <SelectField value={gradTo} placeholder="To" options={rig.joints.map((j) => j.name)} onChange={setGradTo} />
          <SelectField
            value={gradMode}
            options={[
              { value: 'linear', label: 'Linear' },
              { value: 'radial', label: 'Radial' },
            ]}
            onChange={(v) => setGradMode(v as 'linear' | 'radial')}
          />
          <button
            type="button"
            className="ghost small"
            disabled={!selected || !gradFrom || !gradTo}
            onClick={() => {
              const a = jointPos(gradFrom),
                b = jointPos(gradTo);
              if (selected && a && b) run('Gradient', (w, m) => gradient(w, m.positions, selected, a, b, { mode: gradMode, falloff: 'smooth', locked }));
            }}
          >
            Apply
          </button>
        </div>
      </PropRow>
      <PropRow label="Locks" hint="Locked joints keep their weights in every tool and while painting">
        <div className="rig-locks">
          {rig.joints
            .filter((j) => j.deform !== false)
            .map((j) => (
              <button
                key={j.name}
                type="button"
                className={`rig-lock${locked.has(j.name) ? ' is-locked' : ''}`}
                aria-pressed={locked.has(j.name)}
                onClick={() => useRig.setState({ weightLocks: locked.has(j.name) ? locks.filter((n) => n !== j.name) : [...locks, j.name] })}
              >
                {j.name}
              </button>
            ))}
        </div>
      </PropRow>
      {note && <p className="muted rig-help">{note}</p>}
    </Card>
  );
}

/**
 * Auto-rig: fits a human (fingers, twist bones, optional breasts) or
 * quadruped skeleton to the model from cross-sections of its mesh, then a
 * symmetric adjust step and one-click bind.
 */
export function AutoRigCard() {
  const geometry = useRig((s) => s.geometry),
    frame = useRig((s) => s.frame),
    rig = useRig((s) => s.rig);
  const [template, setTemplate] = useState<'human' | 'quadruped'>('human');
  const [fingers, setFingers] = useState(true);
  const [twist, setTwist] = useState(true);
  const [breasts, setBreasts] = useState(false);
  const [toes, setToes] = useState(true);
  const [tail, setTail] = useState(4);
  const [facing, setFacing] = useState<'auto' | '+z' | '-z'>('auto');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  if (!geometry || !frame) return null;
  const fit = () => {
    if (rig.joints.length && !window.confirm('Replace the current joints with a fitted skeleton? Weights and constraints on removed joints are dropped.')) return;
    setBusy(true);
    // Let the button repaint before the (synchronous) fit runs.
    setTimeout(() => {
      try {
        const positions: Vec3[] = [],
          indices: number[] = [];
        geometry.meshes.forEach((m, i) => {
          const base = positions.length;
          positions.push(...(frame.positions[i] ?? []));
          indices.push(...m.indices.map((x) => x + base));
        });
        const result =
          template === 'human'
            ? fitHuman({ positions, indices }, { fingers, twistBones: twist, breasts, toes, facing })
            : fitQuadruped({ positions, indices }, { tailCount: tail, toes });
        const names = new Set(result.joints.map((j) => j.name));
        commit((r) => {
          r.joints = result.joints;
          r.weights = {};
          r.rigidBindings = {};
          r.constraints = (r.constraints ?? []).filter((c) => names.has(c.bone));
          for (const clip of r.clips ?? []) clip.tracks = clip.tracks.filter((t) => names.has(t.joint));
        });
        useRig.getState().select(result.joints[0]?.name ?? null);
        setWarnings(result.warnings);
      } catch (e) {
        setWarnings([e instanceof Error ? e.message : String(e)]);
      } finally {
        setBusy(false);
      }
    }, 10);
  };
  return (
    <Card title="Auto-rig" defaultOpen={!rig.joints.length}>
      <PropRow label="Template">
        <SelectField
          value={template}
          options={[
            { value: 'human', label: 'Human (biped)' },
            { value: 'quadruped', label: 'Quadruped' },
          ]}
          onChange={(v) => setTemplate(v as 'human' | 'quadruped')}
        />
      </PropRow>
      {template === 'human' ? (
        <>
          <div className="rig-check">
            <Toggle checked={fingers} onChange={setFingers} label="Fingers and thumbs" />
          </div>
          <div className="rig-check">
            <Toggle checked={twist} onChange={setTwist} label="Twist bones (arms, legs)" />
          </div>
          <div className="rig-check">
            <Toggle checked={toes} onChange={setToes} label="Toes" />
          </div>
          <div className="rig-check">
            <Toggle checked={breasts} onChange={setBreasts} label="Breast bones" />
          </div>
          <PropRow label="Facing">
            <SelectField
              value={facing}
              options={[
                { value: 'auto', label: 'Detect' },
                { value: '+z', label: '+Z' },
                { value: '-z', label: '−Z' },
              ]}
              onChange={(v) => setFacing(v as typeof facing)}
            />
          </PropRow>
        </>
      ) : (
        <>
          <PropRow label="Tail joints">
            <NumberField value={tail} integer min={0} max={16} onChange={(v) => setTail(Math.max(0, Math.round(v)))} />
          </PropRow>
          <div className="rig-check">
            <Toggle checked={toes} onChange={setToes} label="Toes" />
          </div>
        </>
      )}
      <div className="rig-button-row">
        <button type="button" className="primary small" disabled={busy} onClick={fit}>
          {busy ? 'Fitting…' : 'Fit skeleton to mesh'}
        </button>
      </div>
      <PropRow label="Symmetrize" hint="Copy one side's joints onto the other across the midline">
        <div className="rig-limit">
          <button type="button" className="ghost small" disabled={!rig.joints.length} onClick={() => commit((r) => (r.joints = symmetrize(r.joints, 'L')))}>
            .L → .R
          </button>
          <button type="button" className="ghost small" disabled={!rig.joints.length} onClick={() => commit((r) => (r.joints = symmetrize(r.joints, 'R')))}>
            .R → .L
          </button>
        </div>
      </PropRow>
      <div className="rig-button-row">
        <button
          type="button"
          className="ghost small"
          disabled={!rig.joints.length}
          title="Automatic weights for every mesh, then switch to Skin mode to refine"
          onClick={() => {
            commit((r) => {
              r.weights = {};
              r.rigidBindings = {};
            });
            useRig.setState({ mode: 'skin' });
          }}
        >
          Bind (automatic weights)
        </button>
      </div>
      {warnings.length > 0 && (
        <ul className="rig-help muted">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}
